import { Controller } from "@hotwired/stimulus"
import EmblaCarousel from "embla-carousel"

// Horizontal carousel with navigation buttons and a 1/N counter.
//
// Two modes:
// - Default (loop=false): linear scroll with a hard wrap. At the
//   right edge the next() click snaps scrollLeft back to index 0
//   (behavior:auto). Used on the home page, where cards contain
//   selects + Stimulus controllers we don't want to clone.
// - loop=true: Embla-powered infinite carousel. Embla handles the loop
//   without DOM anchor clones, avoiding accidental navigation from
//   duplicate slide links during wrap timing windows.
export default class extends Controller {
  static targets = ["track", "counter", "prevBtn", "nextBtn"]
  static values = {
    index: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    loop:  { type: Boolean, default: false }
  }

  connect() {
    this.programmaticScroll = false
    // children.length is the source of truth — the ERB sometimes
    // passes a larger collection count to total_value while rendering
    // only the first N (e.g. .limit(10) on @mais_vistos).
    this.originalCount = this.trackTarget.children.length

    if (this.loopValue) {
      this.connectEmbla()
      return
    }

    this.scrollToLogicalIndex(0, { smooth: false })
    this.updateCounter()
    this.updateButtons()
  }

  disconnect() {
    clearTimeout(this._scrollGuardTimeout)
    if (this.embla) {
      this.embla.destroy()
      this.embla = null
    }
    this.element.classList.remove("ford-embla")
    this.trackTarget?.classList.remove("ford-embla__container")
  }

  connectEmbla() {
    this.element.classList.add("ford-embla")
    this.trackTarget.classList.add("ford-embla__container")

    this.embla = EmblaCarousel(this.element, {
      align: "start",
      container: this.trackTarget,
      slides: Array.from(this.trackTarget.children),
      loop: true,
      skipSnaps: false,
      containScroll: false,
      dragFree: false,
      duration: 25
    })

    this.syncEmblaState = this.syncEmblaState.bind(this)
    this.embla.on("select", this.syncEmblaState)
    this.embla.on("reInit", this.syncEmblaState)
    this.syncEmblaState()
  }

  syncEmblaState() {
    if (!this.embla) return

    this.originalCount = this.embla.slideNodes().length
    this.indexValue = this.embla.selectedScrollSnap()
    this.updateCounter()
    this.updateButtons()
  }

  // How many full cards fit in the track right now. Re-measured every
  // time it's read so a window resize (mobile → desktop) updates
  // without a controller reconnect.
  visibleCardCount() {
    if (!this.hasTrackTarget) return 1
    const first = this.trackTarget.children[0]
    if (!first || !first.offsetWidth) return 1
    return Math.max(1, Math.floor(this.trackTarget.clientWidth / first.offsetWidth))
  }

  cardForLogical(i) {
    return this.trackTarget.children[i]
  }

  // Last index whose paging click still reveals a new card in
  // non-loop mode. With one card visible at a time this is
  // originalCount - 1; with N visible it's originalCount - N.
  lastReachableIndex() {
    return Math.max(0, this.originalCount - this.visibleCardCount())
  }

  next() {
    if (this.originalCount <= 1) return
    if (this.embla) {
      this.embla.scrollNext()
      return
    }

    const lastIndex = this.loopValue ? this.originalCount - 1 : this.lastReachableIndex()
    if (this.indexValue >= lastIndex) {
      if (this.originalCount <= this.visibleCardCount()) return
      this.indexValue = 0
      this.scrollToLogicalIndex(0, { smooth: false })
    } else {
      this.indexValue++
      this.scrollToLogicalIndex(this.indexValue)
    }

    this.updateCounter()
    this.updateButtons()
  }

  prev() {
    if (this.originalCount <= 1) return
    if (this.embla) {
      this.embla.scrollPrev()
      return
    }

    if (this.indexValue <= 0) {
      if (this.originalCount <= this.visibleCardCount()) return
      this.indexValue = this.loopValue ? this.originalCount - 1 : this.lastReachableIndex()
      this.scrollToLogicalIndex(this.indexValue, { smooth: false })
    } else {
      this.indexValue--
      this.scrollToLogicalIndex(this.indexValue)
    }

    this.updateCounter()
    this.updateButtons()
  }

  scrollToLogicalIndex(i, opts = {}) {
    this.scrollToChild(this.cardForLogical(i), opts)
  }

  scrollToChild(child, { smooth = true } = {}) {
    if (!this.hasTrackTarget || !child) return
    // Suppress onScroll updates while we're driving the scroll —
    // otherwise the in-progress scrollLeft would re-set indexValue
    // from intermediate positions.
    this.programmaticScroll = true
    clearTimeout(this._scrollGuardTimeout)
    this._scrollGuardTimeout = setTimeout(() => {
      this.programmaticScroll = false
    }, smooth ? 600 : 100)

    this.trackTarget.scrollTo({
      left: child.offsetLeft - this.trackTarget.offsetLeft,
      behavior: smooth ? "smooth" : "auto"
    })
  }

  // Manual swipe path. Find the closest real card and update indexValue
  // from its position.
  onScroll() {
    if (!this.hasTrackTarget) return
    if (this.embla) return
    if (this.programmaticScroll) return

    const track = this.trackTarget
    const scrollLeft = track.scrollLeft
    const children = Array.from(track.children)
    let closestIdx = 0
    let closestDist = Infinity
    children.forEach((child, i) => {
      const dist = Math.abs(child.offsetLeft - scrollLeft)
      if (dist < closestDist) {
        closestDist = dist
        closestIdx = i
      }
    })

    const logical = closestIdx

    if (logical !== this.indexValue) {
      this.indexValue = logical
      this.updateCounter()
      this.updateButtons()
    }
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.indexValue + 1}/${this.originalCount}`
    }
  }

  updateButtons() {
    if (this.embla) {
      const canMove = this.originalCount > 1 && this.embla.scrollSnapList().length > 1
      if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !canMove
      if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !canMove
      return
    }

    // Buttons stay enabled (and loop) as long as there's more than one
    // page of cards. They only disable when the whole set already fits
    // in the visible track — nothing to navigate.
    const canMove = this.originalCount > 1 &&
                    this.originalCount > this.visibleCardCount()
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !canMove
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !canMove
  }
}
